import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  fetchByCategory,
  fetchBySubcategory,
} from "../../store/slices/product-slice";
import { ClipLoader } from "react-spinners";
import SellexProductContainer from "../../components/sellexx-product-container";
import SellexxLogo from "../../components/sellexx-logo";
import CategoryGrid from "../../components/category-grid";
import {
  fetchCategories,
  fetchSubCategories,
} from "../../store/slices/category-slice";
// import CurrencySelector from "../../components/currency-selector";

const defaultCategories = [
  { id: "mens", name: "Mens" },
  { id: "womens", name: "Womens" },
];

const CategoryView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("categoryId");
  const subcategoryId = searchParams.get("subcategoryId");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    items: products,
    loading,
    error,
  } = useSelector((state: RootState) => state.products);

  const {
    items: categories,
    subcategories,
    loading: categoriesLoading,
  } = useSelector((state: RootState) => state.categories);

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState("SAR");

  // Header hide/show on scroll

  useEffect(() => {
    if (!categories || categories.length === 0) {
      dispatch(fetchCategories());
      dispatch(fetchSubCategories());
    }
  }, [dispatch, categories]);
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPosition = window.scrollY;

      if (
        currentScrollPosition > lastScrollPosition &&
        currentScrollPosition > 50
      ) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }

      setLastScrollPosition(currentScrollPosition);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollPosition]);

  // Fetch products
  useEffect(() => {
    if (!categoryId && !subcategoryId) return;
    if (subcategoryId) {
      dispatch(fetchBySubcategory(subcategoryId));
    } else if (categoryId) {
      dispatch(fetchByCategory(categoryId));
    }
  }, [categoryId, subcategoryId, dispatch]);

  const category = subcategoryId
    ? subcategories.find((cat) => cat.id === subcategoryId)
    : categories.find((cat) => cat.id === categoryId);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ClipLoader size={50} color={"#4A90E2"} loading={loading} />
      </div>
    );
  }
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      {/* Header */}
      <header
        className={`top-0 z-50 w-full bg-primary-grey flex items-center justify-center py-4 bg-primary shadow-xl md:px-8 transition-transform duration-300 ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <SellexxLogo className="h-11 lg:h-16" />
      </header>

      {/* Main */}
      <div className="min-h-screen bg-primary-green flex flex-col justify-start items-center">
        <div className="max-w-7xl mx-auto px-4 space-y-12 w-full justify-center items-center">
          {/* ✅ If no query params → show default categories */}
          {!categoryId && !subcategoryId ? (
            <div className="flex flex-col justify-center items-center min-h-[70vh] px-4">
              <h2 className="text-3xl font-bold text-center mb-8">
                Browse by Category
              </h2>
              {categoriesLoading ? (
                <ClipLoader size={40} color={"#4A90E2"} />
              ) : (
                <CategoryGrid
                  categories={categories}
                  alternateStyle={true}
                  bypassLabel={true}
                  onCategoryClick={(val) =>
                    navigate(`/category?categoryId=${val.id}`)
                  }
                />
              )}
            </div>
          ) : products.length === 0 ? (
            // Empty State
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <h2 className="text-4xl font-extrabold text-primary-dark mb-4">
                🧦 {category?.name || "This Collection"} is Coming Soon
              </h2>
              <p className="text-lg text-gray-700 max-w-md leading-relaxed">
                We’re still stitching together our latest styles for the{" "}
                <span className="font-semibold">
                  {category?.name || "category"}
                </span>{" "}
                collection. Check back soon to discover socks that are as comfy
                as they are stylish.
              </p>
              <div className="mt-8 w-16 h-1 bg-primary-dark rounded-full"></div>
            </div>
          ) : (
            // Products List
            <>
              <div className="lg:my-6 lg:mx-0 mx-6 my-4 text-center">
                <h1 className="text-3xl font-bold pb-2">
                  {category?.name || "Category"}
                </h1>
                <p className="text-gray-600">{category?.description}</p>
              </div>
              <section>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 mb-6">
                  {products.map((product) => (
                    <SellexProductContainer
                      key={product.id}
                      selectedCurrency={selectedCurrency}
                      product={product}
                    />
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default CategoryView;
